import { useState } from 'react';
import { Alert } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Small, Body } from '@/components/Typography';
import Badge from '@/components/Badge';
import { CheckrResponse, WorkersV2API } from '@/services/workersV2API';

type Props = {
  workerId: number;
  checkr: CheckrResponse;
  onSync?: () => void;
};

const CheckrCard = ({ workerId, checkr, onSync }: Props) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await WorkersV2API.syncFromCheckr(workerId);
      if (result.success) {
        setSyncResult({
          success: true,
          message: 'Sync completed successfully',
        });
        onSync?.();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed',
        });
      }
    } catch (err) {
      setSyncResult({ success: false, message: 'Failed to sync from Checkr' });
    } finally {
      setSyncing(false);
    }
  };

  if (!checkr.available) {
    return (
      <Card title="Checkr (Background Check)">
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in checkr && checkr.reason
                ? checkr.reason
                : 'error' in checkr && checkr.error
                  ? checkr.error
                  : 'No Checkr data available for this worker'}
            </Small>
            {'candidate_id' in checkr && checkr.candidate_id && (
              <Small color="lighter">Candidate ID: {checkr.candidate_id}</Small>
            )}
            {'checkr_status' in checkr && checkr.checkr_status && (
              <Small color="lighter">Status: {checkr.checkr_status}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card
      actions={[
        {
          label: syncing ? 'Syncing...' : 'Sync',
          onAction: handleSync,
          disabled: syncing,
        },
      ]}
      title="Checkr (Background Check)"
    >
      {syncResult && (
        <Card.Section>
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            onClose={() => setSyncResult(null)}
          >
            {syncResult.message}
          </Alert>
        </Card.Section>
      )}

      <Card.Section>
        <Stack vertical gap={16}>
          <Stack vertical gap={8}>
            <Small color="lighter">Status</Small>
            <Body>{checkr.checkr_status || 'N/A'}</Body>
          </Stack>

          <Stack vertical gap={8}>
            <Small color="lighter">Candidate ID</Small>
            <Body>{checkr.candidate_id}</Body>
          </Stack>

          {checkr.candidate.adjudication && (
            <Stack vertical gap={8}>
              <Small color="lighter">Adjudication</Small>
              <Body>{checkr.candidate.adjudication}</Body>
            </Stack>
          )}

          {checkr.reports && checkr.reports.length > 0 && (
            <Stack vertical gap={8}>
              <Small color="lighter">Reports ({checkr.reports.length})</Small>
              <Stack vertical gap={8}>
                {checkr.reports.map((report) => (
                  <div
                    key={report.id}
                    style={{
                      background: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                    }}
                  >
                    <Stack vertical gap={4}>
                      <Small>
                        <strong>ID:</strong> {report.id}
                      </Small>
                      <Small>
                        <strong>Status:</strong> {report.status}
                      </Small>
                      {report.result && (
                        <Small>
                          <strong>Result:</strong> {report.result}
                        </Small>
                      )}
                      {report.adjudication && (
                        <Small>
                          <strong>Adjudication:</strong> {report.adjudication}
                        </Small>
                      )}
                      {report.completed_at && (
                        <Small>
                          <strong>Completed:</strong>{' '}
                          {new Date(report.completed_at).toLocaleString()}
                        </Small>
                      )}
                    </Stack>
                  </div>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Small color="lighter">Full Data</Small>
          <pre
            style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              lineHeight: '1.5',
              margin: 0,
            }}
          >
            {JSON.stringify(checkr, null, 2)}
          </pre>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default CheckrCard;
